/**
 * Where an item came from, in words — one place, so the badge, the lock note,
 * the edit dialog and the history all say the same thing about the same item.
 *
 * The server is the authority on what is editable (PATCH refuses source fields
 * on any item whose externalSource is not 'manual'); these only explain it.
 */

export type SourceKind = 'manual' | 'csv' | 'jira' | 'other';

export function sourceKind(externalSource: string | null | undefined): SourceKind {
  if (externalSource === 'manual') return 'manual';
  if (externalSource === 'csv') return 'csv';
  if (externalSource === 'jira') return 'jira';
  return 'other';
}

/** Who owns the item's dates, status and owner. */
export function sourceOwnerName(externalSource: string | null | undefined): string {
  switch (sourceKind(externalSource)) {
    case 'jira': return 'Jira';
    case 'manual': return 'this command center';
    default: return 'the master plan export';
  }
}

/** The short note beside a locked status. */
export function lockedNote(externalSource: string | null | undefined): string {
  return sourceKind(externalSource) === 'jira' ? 'managed in Jira' : 'managed by the plan import';
}

/** The edit dialog's description for an item whose source fields are locked. */
export function lockedDescription(externalSource: string | null | undefined): string {
  if (sourceKind(externalSource) === 'jira') {
    return 'This item comes from Jira, so its name, dates, status and owner are owned by Jira. '
      + 'Change them on the issue in Jira and the next sync brings them in. You can still record what happens next.';
  }
  return 'This item came from the master plan export, so its dates, status, and owner are managed there. '
    + 'You can still record what happens next.';
}

/** "· plan import" / "· Jira sync" / "· entered here" in an item's history. */
export function historySourceLabel(eventSource: string, externalSource: string | null | undefined): string {
  if (eventSource === 'human') return 'entered here';
  return sourceKind(externalSource) === 'jira' ? 'Jira sync' : 'plan import';
}

/**
 * A Jira browse link, only if it is one: https://<site>.atlassian.net/browse/KEY.
 * The server already checks this; checking again here means a bad value can
 * never become a clickable link.
 */
export function safeJiraUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  return /^https:\/\/[a-z0-9-]+\.atlassian\.net\/browse\/[A-Z][A-Z0-9_]+-\d+$/.test(url) ? url : null;
}

/** Owner codes the dialog may offer: role codes, not Jira people. */
export function assignableOwnerCodes(owners: Array<{ role_code: string }>): string[] {
  return owners.map((o) => o.role_code).filter((c) => !c.startsWith('jira:'));
}
